import React, { useMemo, useState } from "react";
import {
    CalendarDays,
    Download,
    FileText,
    Snowflake,
    CookingPot,
    ShieldCheck,
} from "lucide-react";
import Page from "../../components/Page";

const REGISTER_OPTIONS = [
    {
        key: "frighi",
        title: "Temperature Frighi",
        desc: "Genera il registro PDF delle temperature dei frigoriferi.",
        icon: Snowflake,
        color: "from-cyan-500 to-blue-600",
        disabled: false,
    },
    {
        key: "abbattimenti",
        title: "Abbattimenti",
        desc: "Genera il registro PDF degli abbattimenti filtrati per periodo.",
        icon: CookingPot,
        color: "from-violet-500 to-fuchsia-600",
        disabled: false,
    },
    {
        key: "sanificazione",
        title: "Sanificazione",
        desc: "Genera il registro PDF della sanificazione filtrato per periodo.",
        icon: ShieldCheck,
        color: "from-emerald-500 to-teal-600",
        disabled: false,
    },
];

const PERIOD_OPTIONS = [
    { key: "day", label: "Ultima giornata" },
    { key: "week", label: "Ultima settimana" },
    { key: "month", label: "Ultimo mese" },
    { key: "custom", label: "Periodo personalizzato" },
];

function todayISO() {
    return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(days) {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
}

function buildPdfUrl({ type, period, dateFrom, dateTo }) {
    const params = new URLSearchParams();
    params.set("period", period);

    if (period === "custom") {
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo) params.set("date_to", dateTo);
    }

    if (type === "frighi") {
        return `/api/frighi/report-azienda-pdf.php?${params.toString()}`;
    }

    if (type === "abbattimenti") {
        return `/api/registri/abbattimenti-report-pdf.php?${params.toString()}`;
    }

    if (type === "sanificazione") {
        return `/api/sanificazione/report-azienda-pdf.php?${params.toString()}`;
    }

    return "#";
}

export default function Registri() {
    const [selectedType, setSelectedType] = useState("frighi");
    const [period, setPeriod] = useState("day");
    const [dateFrom, setDateFrom] = useState(daysAgoISO(7));
    const [dateTo, setDateTo] = useState(todayISO());

    const selectedRegister = useMemo(
        () => REGISTER_OPTIONS.find((x) => x.key === selectedType) || REGISTER_OPTIONS[0],
        [selectedType]
    );

    const pdfUrl = useMemo(() => {
        return buildPdfUrl({
            type: selectedType,
            period,
            dateFrom,
            dateTo,
        });
    }, [selectedType, period, dateFrom, dateTo]);

    function openPdf() {
        if (selectedRegister?.disabled) return;

        if (period === "custom") {
            if (!dateFrom || !dateTo) {
                alert("Seleziona data iniziale e data finale.");
                return;
            }
            if (dateFrom > dateTo) {
                alert("La data iniziale non può essere successiva alla data finale.");
                return;
            }
        }

        window.open(pdfUrl, "_blank", "noopener,noreferrer");
    }

    const Icon = selectedRegister.icon;

    return (
        <Page title="Registri" back>
            <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-8 space-y-6">
                <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                    <div className={`bg-gradient-to-r ${selectedRegister.color} p-6 md:p-8 text-white`}>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider">
                                    <FileText className="w-4 h-4" />
                                    Archivio registri PDF
                                </div>

                                <h1 className="mt-4 text-2xl md:text-4xl font-bold">
                                    Centro Registri
                                </h1>

                                <p className="mt-2 text-sm md:text-base text-white/85 max-w-2xl">
                                    Genera documenti PDF ufficiali con branding aziendale, carta intestata e filigrana automatica se attiva.
                                </p>
                            </div>

                            <div className="w-16 h-16 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center shrink-0">
                                <Icon className="w-8 h-8" />
                            </div>
                        </div>
                    </div>

                    <div className="p-6 md:p-8 grid grid-cols-1 xl:grid-cols-3 gap-6">
                        <div className="xl:col-span-2 space-y-6">
                            <div>
                                <div className="text-sm font-semibold text-slate-500 mb-3">
                                    Scegli il registro
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {REGISTER_OPTIONS.map((item) => {
                                        const Ico = item.icon;
                                        const active = selectedType === item.key;

                                        return (
                                            <button
                                                key={item.key}
                                                type="button"
                                                onClick={() => !item.disabled && setSelectedType(item.key)}
                                                className={[
                                                    "text-left rounded-3xl border p-5 transition-all shadow-sm",
                                                    item.disabled
                                                        ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed opacity-70"
                                                        : active
                                                            ? "border-slate-900 bg-slate-900 text-white"
                                                            : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md",
                                                ].join(" ")}
                                            >
                                                <div className="flex items-center justify-between gap-3">
                                                    <div
                                                        className={[
                                                            "w-12 h-12 rounded-2xl flex items-center justify-center",
                                                            active && !item.disabled
                                                                ? "bg-white/10"
                                                                : "bg-slate-50 border border-slate-200",
                                                        ].join(" ")}
                                                    >
                                                        <Ico
                                                            className={[
                                                                "w-6 h-6",
                                                                active && !item.disabled ? "text-white" : "text-slate-700",
                                                            ].join(" ")}
                                                        />
                                                    </div>

                                                    {item.disabled ? (
                                                        <span className="text-[10px] uppercase tracking-wider font-bold">
                                                            Presto
                                                        </span>
                                                    ) : active ? (
                                                        <span className="text-[10px] uppercase tracking-wider font-bold text-white/80">
                                                            Selezionato
                                                        </span>
                                                    ) : null}
                                                </div>

                                                <div
                                                    className={[
                                                        "mt-4 text-lg font-bold",
                                                        active && !item.disabled ? "text-white" : "text-slate-800",
                                                    ].join(" ")}
                                                >
                                                    {item.title}
                                                </div>

                                                <div
                                                    className={[
                                                        "mt-1 text-sm",
                                                        active && !item.disabled ? "text-white/80" : "text-slate-500",
                                                    ].join(" ")}
                                                >
                                                    {item.desc}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 md:p-6">
                                <div className="flex items-center gap-2 text-slate-800 font-semibold mb-4">
                                    <CalendarDays className="w-5 h-5" />
                                    Periodo del report
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                    {PERIOD_OPTIONS.map((p) => {
                                        const active = period === p.key;
                                        return (
                                            <button
                                                key={p.key}
                                                type="button"
                                                onClick={() => setPeriod(p.key)}
                                                className={[
                                                    "rounded-2xl px-4 py-3 text-sm font-semibold border transition-all",
                                                    active
                                                        ? "border-slate-900 bg-slate-900 text-white"
                                                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100",
                                                ].join(" ")}
                                            >
                                                {p.label}
                                            </button>
                                        );
                                    })}
                                </div>

                                {period === "custom" && (
                                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 mb-2">
                                                Data iniziale
                                            </label>
                                            <input
                                                type="date"
                                                value={dateFrom}
                                                onChange={(e) => setDateFrom(e.target.value)}
                                                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 mb-2">
                                                Data finale
                                            </label>
                                            <input
                                                type="date"
                                                value={dateTo}
                                                onChange={(e) => setDateTo(e.target.value)}
                                                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                                <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">
                                    Anteprima export
                                </div>

                                <div className="mt-3 text-xl font-bold text-slate-800">
                                    {selectedRegister.title}
                                </div>

                                <div className="mt-1 text-sm text-slate-500">
                                    {PERIOD_OPTIONS.find((p) => p.key === period)?.label || "Periodo"}
                                </div>

                                {period === "custom" && (
                                    <div className="mt-3 rounded-2xl bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
                                        Dal <b>{dateFrom || "—"}</b> al <b>{dateTo || "—"}</b>
                                    </div>
                                )}

                                <div className="mt-4 space-y-2 text-sm text-slate-600">
                                    <div>• PDF pronto per stampa</div>
                                    <div>• Carta intestata aziendale</div>
                                    <div>• Filigrana automatica se attiva</div>
                                    <div>• Filtro sul periodo selezionato</div>
                                </div>

                                <button
                                    type="button"
                                    onClick={openPdf}
                                    disabled={!!selectedRegister?.disabled}
                                    className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 text-white px-5 py-3 font-semibold hover:bg-slate-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Download className="w-4 h-4" />
                                    Genera PDF
                                </button>

                                <div className="mt-3 text-xs text-slate-400 break-all">
                                    {selectedRegister?.disabled ? "Modulo non ancora disponibile" : pdfUrl}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Page>
    );
}